﻿import { Checkbox, NumericTextBox, RadioGroup } from '@progress/kendo-react-inputs';
import React, { useEffect } from 'react';
import SettingsSubSection from '../../../../../components/common/SettingsSubSection';
import useDebugFeatures from '../../../../../hooks/use-debug-features';
import { UDViewHistoryColorSource, UDViewTimeDistanceMode } from '../../../../../udView/udview';
import { ColorPickerWithDefault } from '../../../../components/inputs/ColorPickerWithDefault';
import { DropdownListWithValueField } from '../../../../components/inputs/DropdownListWithValueField';
import { useWorkspaceItemContext } from '../../../../contexts/workspace/workspace-item-context';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store-hooks';
import { setSetting } from '../../../../state/slices/settings-slice';
import { RadioButtonList, useFormCheckboxValue, useFormNumericValue } from '../../hooks/forms';
import { useFocusedWorkspaceSelector } from '../../hooks/use-view';
import { useViewCheckboxValue, useViewColorValue, useViewRadioValue } from '../../hooks/view-forms';

const timeDistanceModeOptions: RadioButtonList<UDViewTimeDistanceMode> = [
    { value: UDViewTimeDistanceMode.lastNext, label: 'Last/next' },
    { value: UDViewTimeDistanceMode.allRemaining, label: 'All remaining' }
];

const historyColorSourceOptions: RadioButtonList<UDViewHistoryColorSource> = [
    { value: UDViewHistoryColorSource.history, label: 'History color' },
    { value: UDViewHistoryColorSource.aircraft, label: 'Aircraft color' }
];

const TRACK_HISTORY_DEFAULT_COLOR = '#f0f';
const DEFAULT_COLOR = '#000';
const PROJECTED_POINTS_DEFAULT_COLOR = '#fff';
const ACARS_DEFAULT_COLOR = '#47D45A';
const ADSB_T_DEFAULT_COLOR = '#215E99';
const ADSB_S_DEFAULT_COLOR = '#4D94D8';
const FAA_ANSP_DEFAULT_COLOR = '#000';

export default function PlaneSettings() {
    const color = useViewColorValue((v) => v.plane.settings.historyColor ?? TRACK_HISTORY_DEFAULT_COLOR);
    const defaultColor = useViewColorValue((v) => v.plane.settings.routeFlown.defaultColor);
    const projectedColor = useViewColorValue((v) => v.plane.settings.routeFlown.projected.color);
    const acarsColor = useViewColorValue((v) => v.plane.settings.routeFlown.acars.color);
    const adsbTColor = useViewColorValue((v) => v.plane.settings.routeFlown.adsbTerrestrial.color);
    const adsbSColor = useViewColorValue((v) => v.plane.settings.routeFlown.adsbSatellite.color);
    const faaAnspColor = useViewColorValue((v) => v.plane.settings.routeFlown.faaAnsp.color);

    const projectedEnabled = useViewCheckboxValue((v) => v.plane.settings.routeFlown.projected.enabled);
    const acarsEnabled = useViewCheckboxValue((v) => v.plane.settings.routeFlown.acars.enabled);
    const adsbTEnabled = useViewCheckboxValue((v) => v.plane.settings.routeFlown.adsbTerrestrial.enabled);
    const adsbSEnabled = useViewCheckboxValue((v) => v.plane.settings.routeFlown.adsbSatellite.enabled);
    const faaAnspEnabled = useViewCheckboxValue((v) => v.plane.settings.routeFlown.faaAnsp.enabled);

    const projectedPoints = useViewCheckboxValue((v) => v.plane.settings.showProjectedPoints);
    const coastMode = useViewCheckboxValue((v) => v.plane.settings.enableCoastMode);
    const historyModern = useViewCheckboxValue((v) => v.plane.settings.historyModern);
    const historyColorSource = useViewRadioValue(historyColorSourceOptions, (v) => v.plane.settings.historyColorSource);
    const timeDistanceMode = useViewRadioValue(timeDistanceModeOptions, (v) => v.plane.settings.udViewTimeDistanceMode);
    const debug = useDebugFeatures();

    return (
        <div className="d-flex flex-column gap-2 px-3 py-2 pt-0">
            <SettingsSubSection title="Track history">
                <div className="d-flex align-items-center gap-2">
                    <div>Track history color</div>
                    <ColorPickerWithDefault {...color} views={['palette', 'gradient']} defaultColor={TRACK_HISTORY_DEFAULT_COLOR} />
                </div>
                <Checkbox {...historyModern} className="flex-grow-1" label={'Modern'}></Checkbox>
                <RadioGroup {...historyColorSource} layout={'horizontal'} />
            </SettingsSubSection>
            <SettingsSubSection title="Aircraft movement">
                <Checkbox {...coastMode} className="flex-grow-1" label={'Enable coast mode'} id="layers-coast-mode"></Checkbox>
            </SettingsSubSection>
            <SettingsSubSection title="Time/distance display">
                <RadioGroup {...timeDistanceMode} layout={'horizontal'} />
            </SettingsSubSection>
            <SettingsSubSection title="Route flown" >
                <Checkbox {...projectedPoints} className="flex-grow-1" label={'Show projected points'}></Checkbox>
            </SettingsSubSection>
            <SettingsSubSection title="Flown points colors" id="layers-aircraft-route-flown-setting">
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, max-content)',
                        rowGap: '.25rem',
                        alignItems: 'center'
                    }}>
                    <div style={{ gridColumn: '1/3' }}>Default</div>
                    <ColorPickerWithDefault {...defaultColor} defaultColor={DEFAULT_COLOR} />

                    <Checkbox {...projectedEnabled} />
                    <div className="px-2">Projected</div>
                    <ColorPickerWithDefault {...projectedColor} defaultColor={PROJECTED_POINTS_DEFAULT_COLOR} />

                    <Checkbox {...acarsEnabled} />
                    <div className="px-2">ACARS/3PDI</div>
                    <ColorPickerWithDefault {...acarsColor} defaultColor={ACARS_DEFAULT_COLOR} />

                    <Checkbox {...adsbTEnabled} />
                    <div className="px-2">ADS-B (terrestrial)</div>
                    <ColorPickerWithDefault {...adsbTColor} defaultColor={ADSB_T_DEFAULT_COLOR} />

                    <Checkbox {...adsbSEnabled} />
                    <div className="px-2">ADS-B (satellite)</div>
                    <ColorPickerWithDefault {...adsbSColor} defaultColor={ADSB_S_DEFAULT_COLOR} />

                    <Checkbox {...faaAnspEnabled} />
                    <div className="px-2">FAA/ANSP</div>
                    <ColorPickerWithDefault {...faaAnspColor} defaultColor={FAA_ANSP_DEFAULT_COLOR} />
                </div>
            </SettingsSubSection>
            {debug && (
                <>
                    <SettingsSubSection title="Icons">
                        <IconDebugPanel />
                    </SettingsSubSection>
                    <SettingsSubSection title="Coast debug">
                        <CoastModeDebugPanel />
                    </SettingsSubSection>
                    <SettingsSubSection title="Route flown debug">
                        <RouteFlownDebugPanel />
                    </SettingsSubSection>
                </>
            )}
        </div>
    );
}

function RouteFlownDebugPanel() {
    const facade = useWorkspaceItemContext().flightLayerFacade;
    const [algorithm, setAlgorithm] = React.useState(facade.routes.flownRouteParams.algorithm);
    const [segments, setSegments] = React.useState(facade.routes.flownRouteParams.numberOfSegments);
    const [alpha, setAlpha] = React.useState(facade.routes.flownRouteParams.alpha);

    useEffect(() => {
        facade.routes.flownRouteParams = {
            algorithm,
            numberOfSegments: segments,
            alpha
        };
    }, [facade, algorithm, segments, alpha]);

    return (
        <div>
            <div>Algorithm</div>
            <DropdownListWithValueField
                data={[
                    { value: 'catmull-rom', text: 'Catmull-Rom' },
                    { value: 'cubic-spline', text: 'Cubic spline interpolation' }
                ]}
                value={algorithm}
                onChange={(e) => setAlgorithm(e.value as any)}
            />
            <div className="mt-2">Number of segments</div>
            <NumericTextBox value={segments} onChange={(e) => setSegments(Math.max(1, e.value as number))} />
            <div className="mt-2">Catmull-Alpha Alpha</div>
            <NumericTextBox value={alpha} onChange={(e) => setAlpha(Math.max(1, e.value as number))} />
        </div>
    );
}
function IconDebugPanel() {
    const facade = useWorkspaceItemContext().flightLayerFacade;
    const currentSdf = facade.deckPlugin.planeSdfParams;
    const cutoff = useFormNumericValue(currentSdf.cutoff);
    const softEnabled = useFormCheckboxValue(currentSdf.softEnabled);
    const softMin = useFormNumericValue(currentSdf.softEdgeMin);
    const softMax = useFormNumericValue(currentSdf.softEdgeMax);
    const outlineEnabled = useFormCheckboxValue(currentSdf.softEnabled);
    const outlineMin0 = useFormNumericValue(currentSdf.outlineMin0);
    const outlineMin1 = useFormNumericValue(currentSdf.outlineMin1);
    const outlineMax0 = useFormNumericValue(currentSdf.outlineMax0);
    const outlineMax1 = useFormNumericValue(currentSdf.outlineMax1);

    useEffect(() => {
        facade.deckPlugin.planeSdfParams = {
            cutoff: cutoff.value!,
            softEnabled: softEnabled.value,
            softEdgeMin: softMin.value!,
            softEdgeMax: softMax.value!,
            outlineEnabled: outlineEnabled.value,
            outlineMin0: outlineMin0.value!,
            outlineMin1: outlineMin1.value!,
            outlineMax0: outlineMax0.value!,
            outlineMax1: outlineMax1.value!
        };
        facade.deckPlugin.updateLayers();
    }, [facade, cutoff, softMin, softMax, outlineMin0, outlineMin1, outlineMax0, outlineMax1, softEnabled, outlineEnabled]);

    return (
        <div className="d-flex flex-column gap-2 font-mono">
            <div>
                Cutoff
                <NumericTextBox {...cutoff} step={0.1} />
            </div>

            <Checkbox label={'Soft edge'} {...softEnabled} />
            <div className="d-flex gap-2">
                <div>
                    SOFT_EDGE_MIN
                    <NumericTextBox {...softMin} step={0.1} />
                </div>

                <div>
                    SOFT_EDGE_MAX
                    <NumericTextBox {...softMax} step={0.1} />
                </div>
            </div>

            <Checkbox label={'Outline'} {...outlineEnabled} />
            <div className="d-flex gap-2">
                <div>
                    OUTLINE_MIN_VALUE0
                    <NumericTextBox {...outlineMin0} step={0.1} />
                </div>
                <div>
                    OUTLINE_MIN_VALUE1
                    <NumericTextBox {...outlineMin1} step={0.1} />
                </div>
            </div>

            <div className="d-flex gap-2">
                <div>
                    OUTLINE_MAX_VALUE0
                    <NumericTextBox {...outlineMax0} step={0.1} />
                </div>

                <div>
                    OUTLINE_MAX_VALUE1
                    <NumericTextBox {...outlineMax1} step={0.1} />
                </div>
            </div>
        </div>
    );
}

function CoastModeDebugPanel() {
    const facade = useWorkspaceItemContext().flightLayerFacade;
    const [value, setValue] = React.useState<Float32Array | null>(null);
    const selectedFlight = useFocusedWorkspaceSelector((x) => x.flights.selected[0]?.details);
    const coastDebug = useAppSelector((x) => x.settings.coastModeDebugOverlay);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!selectedFlight) {
            return;
        }
        const fid = selectedFlight.id;

        const int = self.setInterval(() => {
            setValue(
                (facade.deckPlugin.overlay as any)._deck.device.readPixelsToArrayWebGL(facade.deckPlugin.positionTexture!, {
                    sourceX: fid % 320,
                    sourceY: Math.trunc(fid / 320),
                    sourceWidth: 1,
                    sourceHeight: 1
                })
            );
        }, 25);

        return () => {
            self.clearInterval(int);
        };
    }, [selectedFlight]);

    return (
        <div className="d-flex flex-column gap-2">
            <div>
                <div>
                    <Checkbox
                        checked={coastDebug}
                        onChange={(e) => dispatch(setSetting('coastModeDebugOverlay', e.value))}
                        className="flex-grow-1"
                        label={'Enable coast mode debug overlay'}></Checkbox>
                    <div>Mode:</div>
                    <RadioGroup
                        value={facade.deckPlugin.deckCoastDebugProps.coastTargetMode}
                        onChange={(e) => {
                            facade.deckPlugin.deckCoastDebugProps.coastTargetMode = e.value;
                            facade.deckPlugin.updateLayers();
                        }}
                        data={[
                            {
                                value: 0,
                                label: 'Coast from last position to current'
                            },
                            {
                                value: 1,
                                label: 'Coast from current position to current'
                            }
                        ]}></RadioGroup>
                    <div className="pt-2">Max project forward time [s]</div>
                    <NumericTextBox
                        step={0.001}
                        value={facade.deckPlugin.deckCoastDebugProps.maxProjectForwardTimeSeconds}
                        onChange={(e) => {
                            facade.deckPlugin.deckCoastDebugProps.maxProjectForwardTimeSeconds = e.value as number;
                            facade.deckPlugin.updateLayers();
                        }}
                    />

                    <div>Min to target coast [deg]</div>
                    <NumericTextBox
                        step={1}
                        value={facade.deckPlugin.deckCoastDebugProps.minToTargetJump}
                        onChange={(e) => {
                            facade.deckPlugin.deckCoastDebugProps.minToTargetJump = e.value as number;
                            facade.deckPlugin.updateLayers();
                        }}
                    />

                    <div>Max to target coast [deg]</div>
                    <NumericTextBox
                        step={0.01}
                        value={facade.deckPlugin.deckCoastDebugProps.maxToTargetJump}
                        onChange={(e) => {
                            facade.deckPlugin.deckCoastDebugProps.maxToTargetJump = e.value as number;
                            facade.deckPlugin.updateLayers();
                        }}
                    />
                </div>
            </div>

            <div>
                <div>Flight position viewer</div>
                <div>
                    {selectedFlight?.acid ?? '??'} ({selectedFlight?.id ?? '??'})
                </div>
                <div className="d-flex flex-column gap-0">
                    <code>x: {value?.[0]}</code>
                    <code>y: {value?.[1]}</code>
                    <code>z: {value?.[2]}</code>
                    <code>w: {value?.[3]}</code>
                </div>
            </div>
        </div>
    );
}
